import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { Box, IconButton, InputAdornment, Stack, TextField } from "@mui/material";
import { useTu } from "@src/appV2/i18n/useTu";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { useIsMobile } from "@src/appV2/redesign/AppLayout/useResponsive";
import clamp from "lodash/clamp";
import { useEffect, useRef, useState } from "react";

import { Text } from "../../../components/Text";

interface ChargeRateControlsProps {
  /** Title of this charge rate */
  title: string;
  /** Current rate in dollars (major units) */
  rate: number;
  /** Minimum rate in dollars (major units) */
  minRate: number;
  /** Maximum rate in dollars (major units) */
  maxRate: number;
  /** Callback when rate changes */
  onRateChange: (rate: number) => void;
  /** Override label for the min boundary (default: "Min rate") */
  minRateLabel?: string;
  /** Override the displayed value for the min boundary (default: minRate) */
  minRateDisplayValue?: number;
  /** Override label for the max boundary (default: "Max rate") */
  maxRateLabel?: string;
  /** Override the displayed value for the max boundary (default: maxRate) */
  maxRateDisplayValue?: number;
}

const RATE_INCREMENT = 1; // $1.00 increment

/**
 * Validates input string as a valid currency value
 */
function isValidCurrencyInput(value: string): boolean {
  // Allow empty, numbers, and numbers with up to 2 decimal places
  if (value === "" || value === ".") {
    return true;
  }
  const parsedNumber = Number(value);
  const currencyPattern = /^\d*\.?\d{0,2}$/;
  return !Number.isNaN(parsedNumber) && currencyPattern.exec(value) !== null;
}

/**
 * Controls for adjusting the charge rate with +/- buttons and direct input.
 */
export function ChargeRateControls(props: ChargeRateControlsProps) {
  const { tu } = useTu("schedule");
  const { rate, minRate, maxRate, title, onRateChange } = props;
  const { minRateLabel, minRateDisplayValue, maxRateLabel, maxRateDisplayValue } = props;

  const isMobile = useIsMobile();
  const [inputValue, setInputValue] = useState(rate.toFixed(2));
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isFocused) {
      setInputValue(rate.toFixed(2));
    }
  }, [rate, isFocused]);

  const handleDecrease = () => {
    const newRate = clamp(rate - RATE_INCREMENT, minRate, maxRate);
    onRateChange(newRate);
  };

  const handleIncrease = () => {
    const newRate = clamp(rate + RATE_INCREMENT, minRate, maxRate);
    onRateChange(newRate);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (!isValidCurrencyInput(value)) {
      return;
    }

    setInputValue(value);

    // Only update rate if we have a valid number
    const numericValue = Number.parseFloat(value);
    if (!Number.isNaN(numericValue)) {
      const clampedValue = clamp(numericValue, minRate, maxRate);
      onRateChange(clampedValue);
    }
  };

  const handleInputBlur = () => {
    setIsFocused(false);
    // Format the value on blur
    const numericValue = Number.parseFloat(inputValue);
    if (Number.isNaN(numericValue)) {
      setInputValue(rate.toFixed(2));
    } else {
      const clampedValue = clamp(numericValue, minRate, maxRate);
      setInputValue(clampedValue.toFixed(2));
      onRateChange(clampedValue);
    }
  };

  const isAtMin = rate <= minRate;
  const isAtMax = rate >= maxRate;

  const rateLabelNode = (label: string, displayValue: number, defaultLabel: string) => (
    <Box sx={{ minWidth: 60, textAlign: isMobile ? "center" : "left" }}>
      <Text variant="caption" sx={(theme) => ({ color: theme.palette.text.disabled })}>
        {label || defaultLabel}
      </Text>
      <Text semibold variant="body2">
        {formatDollarsAsUsd(displayValue)}
      </Text>
    </Box>
  );

  const minRateLabelNode = rateLabelNode(
    minRateLabel ?? "",
    minRateDisplayValue ?? minRate,
    tu("Min rate")
  );
  const maxRateLabelNode = rateLabelNode(
    maxRateLabel ?? "",
    maxRateDisplayValue ?? maxRate,
    tu("Max rate")
  );

  const decreaseButton = (
    <IconButton
      aria-label={tu("Decrease rate")}
      disabled={isAtMin}
      size="small"
      sx={(theme) => ({
        border: `1px solid ${theme.palette.border?.base}`,
        "&:disabled": { opacity: 0.5 },
      })}
      onClick={handleDecrease}
    >
      <RemoveIcon fontSize="small" />
    </IconButton>
  );

  const increaseButton = (
    <IconButton
      aria-label={tu("Increase rate")}
      disabled={isAtMax}
      size="small"
      sx={(theme) => ({
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        "@media (hover: hover)": {
          "&:hover": { backgroundColor: theme.palette.primary.dark },
        },
        "&:disabled": {
          backgroundColor: theme.palette.action.disabledBackground,
          color: theme.palette.action.disabled,
        },
      })}
      onClick={handleIncrease}
    >
      <AddIcon fontSize="small" />
    </IconButton>
  );

  const rateInput = (
    <TextField
      inputRef={inputRef}
      size="medium"
      value={inputValue}
      InputProps={{
        startAdornment: <InputAdornment position="start">$</InputAdornment>,
        endAdornment: <InputAdornment position="end">/hr</InputAdornment>,
      }}
      // eslint-disable-next-line react/jsx-no-duplicate-props -- inputProps is for HTML attrs, InputProps is for MUI component props
      inputProps={{
        "aria-label": tu("Charge rate"),
        inputMode: "decimal",
        style: { textAlign: "center", width: 60 },
      }}
      sx={(theme) => ({
        "& .MuiOutlinedInput-root": { borderRadius: theme.borderRadius?.large },
      })}
      onBlur={handleInputBlur}
      onChange={handleInputChange}
      onFocus={() => {
        setIsFocused(true);
      }}
    />
  );

  if (isMobile) {
    return (
      <Box>
        <Text semibold variant="body2" sx={{ marginBottom: 3 }}>
          {title}
        </Text>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Stack alignItems="center" spacing={1}>
            {decreaseButton}
            {minRateLabelNode}
          </Stack>
          {rateInput}
          <Stack alignItems="center" spacing={1}>
            {increaseButton}
            {maxRateLabelNode}
          </Stack>
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      <Text semibold variant="body2" sx={{ marginBottom: 3 }}>
        {title}
      </Text>
      <Stack direction="row" alignItems="center" spacing={3} justifyContent="space-between">
        {minRateLabelNode}
        <Stack direction="row" alignItems="center" spacing={3}>
          {decreaseButton}
          {rateInput}
          {increaseButton}
        </Stack>
        {maxRateLabelNode}
      </Stack>
    </Box>
  );
}
